<template>
  <div>
    <Head :title="form.name" />
    <h1 class="mb-8 text-3xl font-bold">
      Transaksi
      <span class="text-indigo-400 font-medium"> / </span>
      Penerimaan Barang PO
    </h1>

    <div class="flex items-center justify-between mb-6">
      <search-filter
        v-model="form.search"
        class="mr-4 w-full max-w-md"
        @reset="reset"
        input-placeholder="Search by No.Penerimaan">
        <label class="block text-gray-700">Project:</label>
        <select v-model="form.project" class="form-select mt-1 w-full">
          <option :value="null">
            [ALL]
          </option>
          <option
            v-for="project in projects"
            :key="project.id"
            :value="project.id">
            {{ project.title }}
          </option>
        </select>
      </search-filter>
      <div class="flex mx-6 w-full bg-white rounded shadow">
        <vSelect
          v-model="form.item"
          :reduce="(item) => item.id"
          class="min-w-full focus:text-indigo-500"
          placeholder="[Filter by barang detail...]"
          label="name"
          :filterable="false"
          :options="options"
          :append-to-body="true"
          @search="onSearchItem" />
      </div>
      <Link class="btn-indigo" href="/porecv/create">
        <span>Create</span>
        <span class="hidden md:inline">&nbsp;Penerimaan Barang PO</span>
      </Link>
    </div>

    <div class="w-full bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <thead>
          <tr class="text-left font-bold">
            <th class="pb-4 pt-6 px-4">
              No. Penerimaan
            </th>
            <th class="pb-4 pt-6 px-4">
              No. PO
            </th>
            <th class="px-6 pt-6 pb-4">
              Lampiran
            </th>
            <th class="pb-4 pt-6 px-4">
              Project
            </th>
            <th class="pb-4 pt-6 px-4">
              Activity
            </th>
            <th class="pb-4 pt-6 px-4">
              Vendor
            </th>
            <th class="pb-4 pt-6 px-4">
              Tanggal Terima
            </th>
            <th class="pb-4 pt-6 px-4">
              Status
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="porecv in purchaseOrderRecv.data"
            :key="porecv.id"
            class="focus-within:bg-gray-100 hover:bg-gray-100">
            <td class="border-t">
              <Link class="flex items-center p-4 focus:text-indigo-500" :href="`/porecv/${porecv.id}/edit`">
                {{ porecv.porecv_code }}
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center p-4 focus:text-indigo-500" :href="`/porecv/${porecv.id}/edit`">
                {{ porecv.po_code }}
              </Link>
            </td>
            <td class="border-t">
              <Link
                class="flex py-4 px-10"
                :href="`/porecv/${porecv.id}/edit#attachments`"
                tabindex="-1">
                <Icon name="attachment" v-if="porecv.has_attachment" />
              </Link>
            </td>
            <td class="whitespace-normal border-t">
              <Link class="flex items-center p-4 focus:text-indigo-500" :href="`/porecv/${porecv.id}/edit`">
                {{ porecv.project }}
              </Link>
            </td>
            <td class="whitespace-normal border-t">
              <Link class="flex items-center p-4 focus:text-indigo-500" :href="`/porecv/${porecv.id}/edit`">
                {{ porecv.project_activity }}
              </Link>
            </td>
            <td class="whitespace-normal border-t">
              <Link class="flex items-center p-4 focus:text-indigo-500" :href="`/porecv/${porecv.id}/edit`">
                {{ porecv.supplier }}
              </Link>
            </td>
            <td class="whitespace-normal border-t">
              <Link class="flex items-center p-4 focus:text-indigo-500" :href="`/porecv/${porecv.id}/edit`">
                {{ formatDate(porecv.recv_date) }}
              </Link>
            </td>
            <td class="whitespace-normal border-t">
              <Link class="flex items-center p-4 focus:text-indigo-500" :href="`/porecv/${porecv.id}/edit`">
                {{ porecv.status }}
              </Link>
            </td>
            <td class="whitespace-normal border-t">
              <a
                v-if="porecv.status === 'LOCKED'"
                :href="`/porecv/${porecv.id}/print`"
                as="button"
                target="_blank"
                class="p-2 text-white whitespace-nowrap text-sm font-bold focus:bg-green-400 bg-green-500 hover:bg-orange-400 rounded m-2">
                Print </a>
            </td>
          </tr>
          <tr v-if="purchaseOrderRecv.data.length === 0">
            <td class="p-4 border-t" colspan="7">
              No purchase order receive data found.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <pagination class="mt-6" :links="purchaseOrderRecv.links" />
  </div>
</template>

<script lang="js">
import { Head, Link } from '@inertiajs/vue3'
import { ref } from 'vue'
import { format, parse } from 'date-fns'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination'
import pickBy from 'lodash/pickBy'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import SearchFilter from '@/Shared/SearchFilter'
import Icon from '@/Shared/Icon'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

export default {
    components: {
        Head,
        Link,
        Pagination,
        SearchFilter,
        vSelect,
        Icon,
    },
    layout: Layout,
    props: {
        purchaseOrderRecv: Object,
        filters: Object,
        projects: Object,
        defaultOptions: Object,
    },
    remember: 'form',
    setup (props) {
        const options = ref(props.defaultOptions)
        const searchAutoComplete = throttle((loading, search) => {
            fetch(`/items?options=1&search=${encodeURI(search)}`).then((res) => {
                res.json().then((json) => (options.value = json.items))
                loading(false)
            })
        }, 500)
        const onSearchItem = (search, loading) => {
            if (search.length) {
                loading(true)
                searchAutoComplete(loading, search)
            }
        }
        return {
            options,
            onSearchItem,
        }
    },
    data () {
        return {
            form: {
                search: this.filters.search,
            },
        }
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function () {
                this.$inertia.get('/porecv', pickBy(this.form), { preserveState: true })
            }, 150),
        },
    },
    methods: {
        reset () {
            this.form = mapValues(this.form, () => null)
        },
        formatNumber (num) {
            return num ? num.toLocaleString('id-ID') : '0'
        },
        formatDate (ymd) {
            return format(parse(ymd, 'yyyy-MM-dd', new Date()), 'dd-MMM-yyyy')
        },
    },
}
</script>
